import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import { TryAlbumListTemplate } from '../../components/Templates';
import { AlbumCellProps } from '../../components/Molecules';

import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import { useUserStore } from '../../stores/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { AlbumListType, SpaceListType } from '../../models/SpaceStore';
import { arrayEqual } from '../../utils/array';
import { DEFAULT_TRY_CLASSROOMID } from '../../constants/appInfo';
import { SpaceMembershipOperation } from '../../api/Rest/space';

const { fetchSpace, changeUserSpaceMembership } = useSpaceStore.getState();

const TryAlbumListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { classroomId: tryClassroomId } = useParams();
  const activeUserId = useSessionStore((store) => store.activeUserId);

  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  useEffect(() => {
    if (!activeUserId || !userProfile) {
      navigate('/start');
    }
  }, [activeUserId, navigate, userProfile]);

  useEffect(() => {
    if (!tryClassroomId || tryClassroomId !== DEFAULT_TRY_CLASSROOMID) {
      navigate('/smart-parents');
    }
  }, [navigate, tryClassroomId]);

  useSpaceStoreListField('spaces', SpaceListType.Recommended);

  useEffect(() => {
    if (tryClassroomId) {
      fetchSpace(SpaceListType.Recommended, tryClassroomId).catch(
        console.error,
      );
    }
  }, [tryClassroomId]);

  const { tryClassroom, tryClassroomMembership } = useSpaceStore((store) => {
    const space = store.getSpace(tryClassroomId ?? '');
    const membership = store.getActiveUserMembership(tryClassroomId ?? '');
    return {
      tryClassroom: space,
      tryClassroomMembership: membership,
    };
  }, shallow);

  // TODO: need to be async?
  const visitClassroom = useCallback(() => {
    changeUserSpaceMembership(tryClassroomId ?? '', {
      operation: SpaceMembershipOperation.Visit,
    }).catch(console.error);
  }, [tryClassroomId]);

  useEffect(() => {
    if (tryClassroomMembership === null) {
      visitClassroom();
    }
  }, [tryClassroomMembership, visitClassroom]);

  const albumNormalField = useSpaceStoreListField(
    'albums',
    AlbumListType.TryAlbum,
  );

  const fetchAlbums = useCallback<FetchListFn>(
    (cursor) =>
      useSpaceStore.getState().fetchAlbums(
        {
          type: AlbumListType.TryAlbum,
          classroomId: tryClassroomId ?? '',
        },
        cursor,
      ),
    [tryClassroomId],
  );

  const {
    fetchMore: fetchMoreAlbums,
    refresh: refreshAlbums,
    isFetchingMore,
    isFinished,
  } = useFetchList(albumNormalField, fetchAlbums);

  useEffect(() => {
    if (tryClassroomId) {
      refreshAlbums().catch(console.error);
    }
  }, [refreshAlbums, tryClassroomId]);

  const loadMoreItems = useCallback(() => {
    fetchMoreAlbums().catch(console.error);
  }, [fetchMoreAlbums]);

  const handleScroll = useCallback(() => {
    const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
    if (
      scrollTop + clientHeight >= scrollHeight - 152 && // 152 is the footer
      !isFetchingMore &&
      !isFinished
    ) {
      loadMoreItems();
    }
  }, [isFetchingMore, isFinished, loadMoreItems]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const onClickAlbum = useCallback(
    (gameSetId: string) => {
      navigate(`/club/${tryClassroomId ?? ''}/journey/${gameSetId}`);
    },
    [navigate, tryClassroomId],
  );

  const albums = useSpaceStore((spaceStoreState) => {
    return spaceStoreState.getAlbums(AlbumListType.TryAlbum);
  }, arrayEqual(shallow));

  const albumData = useMemo<AlbumCellProps[]>(() => {
    return albums.map((album) => {
      return {
        album: album,
        albumClassroom: tryClassroom,
        onClick: () => {
          onClickAlbum(album.gameSetId);
        },
      };
    });
  }, [albums, onClickAlbum, tryClassroom]);

  return userProfile ? <TryAlbumListTemplate albumData={albumData} /> : null;
};

export default TryAlbumListScreen;
